import React, { useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity } from 'lucide-react';

/**
 * CalendarHeatmap — GitHub-style 365-day contribution graph for
 * workout volume. Each day is a small square; colour intensity
 * encodes the volume logged that day. Hover shows the date and
 * stats; click pins a tooltip.
 *
 * Layout: 53 columns (weeks) × 7 rows (Mon–Sun). Columns flow
 * left-to-right with the most-recent week on the right.
 *
 * Props:
 *   days       — array of { date, volume, intensity, workouts, muscles }
 *                matching the backend /api/insights/calendar-heatmap
 *                response shape. Optional; if missing, renders all
 *                zeros.
 *   onDayClick — optional (day) => void
 *   startDate  — ISO date for the leftmost column
 *   endDate    — ISO date for the rightmost column
 */
export const CalendarHeatmap = ({ days = [], onDayClick }) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(null);  // { date, x, y }
  const [pinned, setPinned] = useState(null);
  const containerRef = useRef(null);

  // Group days into weeks (53 columns × 7 rows). The first column
  // is the week containing start_date; we pad before it to align
  // weekdays.
  const grid = useMemo(() => buildGrid(days), [days]);

  const handleEnter = (day, e) => {
    if (!day) return;
    const rect = containerRef.current?.getBoundingClientRect();
    const x = (e?.clientX ?? 0) - (rect?.left ?? 0);
    const y = (e?.clientY ?? 0) - (rect?.top ?? 0);
    setHovered({ ...day, x, y });
  };

  const handleClick = (day) => {
    if (!day) return;
    setPinned((p) => (p?.date === day.date ? null : day));
    onDayClick?.(day);
  };

  return (
    <div
      ref={containerRef}
      className="relative"
      data-testid="calendar-heatmap"
    >
      <div className="overflow-x-auto pb-2">
        <svg
          viewBox={`0 0 ${grid.cols * (CELL + GAP) + ROW_LABEL_W} ${7 * (CELL + GAP) + DAY_LABEL_H}`}
          xmlns="http://www.w3.org/2000/svg"
          className="block min-w-full"
          data-testid="calendar-heatmap-svg"
        >
          {/* Day-of-week labels (Mon / Wed / Fri) */}
          {DAY_LABELS.map((label, row) => (
            <text
              key={label}
              x={0}
              y={DAY_LABEL_H + row * (CELL + GAP) + CELL - 1}
              className="fill-[var(--color-text-muted)]"
              style={{ fontSize: 9, letterSpacing: 1, textTransform: 'uppercase' }}
            >
              {label}
            </text>
          ))}

          {/* Month labels along the top */}
          {grid.monthLabels.map(({ x, label }) => (
            <text
              key={`${x}-${label}`}
              x={ROW_LABEL_W + x}
              y={DAY_LABEL_H - 6}
              className="fill-[var(--color-text-muted)]"
              style={{ fontSize: 9, letterSpacing: 1, textTransform: 'uppercase' }}
            >
              {label}
            </text>
          ))}

          {/* Day cells */}
          {grid.cells.map((cell) => {
            if (!cell.day) {
              return null;
            }
            const isHovered = hovered?.date === cell.day.date;
            const isPinned = pinned?.date === cell.day.date;
            return (
              <rect
                key={cell.day.date}
                x={ROW_LABEL_W + cell.col * (CELL + GAP)}
                y={DAY_LABEL_H + cell.row * (CELL + GAP)}
                width={CELL}
                height={CELL}
                rx={2}
                ry={2}
                fill={intensityFill(cell.day.intensity, cell.day.workouts > 0)}
                stroke={
                  isPinned
                    ? 'rgba(213,255,123,0.95)'
                    : isHovered
                    ? 'rgba(213,255,123,0.5)'
                    : 'rgba(255,255,255,0.04)'
                }
                strokeWidth={isPinned ? 1.5 : isHovered ? 1 : 0.5}
                onMouseEnter={(e) => handleEnter(cell.day, e)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => handleClick(cell.day)}
                style={{ cursor: 'pointer' }}
                data-testid={`heatmap-day-${cell.day.date}`}
                data-volume={cell.day.volume}
                data-workouts={cell.day.workouts}
                data-intensity={cell.day.intensity}
              />
            );
          })}
        </svg>
      </div>

      {/* Hover tooltip */}
      {(hovered || pinned) && (
        <Tooltip
          day={pinned || hovered}
          t={t}
          onClose={pinned ? () => setPinned(null) : null}
        />
      )}
    </div>
  );
};

// ===========================================================================
// Sub-components
// ===========================================================================

function Tooltip({ day, t, onClose }) {
  if (!day) return null;
  const isPinned = !!onClose;
  const noWorkout = day.workouts === 0;
  return (
    <div
      className="absolute top-3 right-3 z-20 max-w-xs"
      data-testid="heatmap-tooltip"
    >
      <div className="rounded-2xl border border-[var(--color-border)] bg-[#0a1208]/95 backdrop-blur-sm p-3 shadow-2xl">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
              {formatDate(day.date, t)}
            </p>
            {noWorkout ? (
              <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                {t('calendarHeatmap.tooltip.rest', { defaultValue: 'Rest day' })}
              </p>
            ) : (
              <>
                <div className="mt-1 flex items-center gap-2">
                  <Activity className="w-3.5 h-3.5 text-primary-200" />
                  <span className="text-sm text-[var(--color-text)] font-display">
                    {day.workouts} {t('calendarHeatmap.tooltip.workouts', { defaultValue: day.workouts === 1 ? 'workout' : 'workouts' })}
                  </span>
                </div>
                <div className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                  {Math.round(day.volume).toLocaleString()} {t('calendarHeatmap.tooltip.volume', { defaultValue: 'volume' })}
                </div>
                {day.muscles?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {day.muscles.map((m) => (
                      <span
                        key={m}
                        className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] uppercase tracking-[0.12em] border border-[rgba(213,255,123,0.3)] bg-[rgba(213,255,123,0.08)] text-primary-100"
                      >
                        {t(`muscleMap.muscle.${m}`, { defaultValue: m })}
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          {isPinned && (
            <button
              type="button"
              onClick={onClose}
              className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              data-testid="heatmap-tooltip-close"
            >
              ✕
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ColorScale({ t }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-[var(--color-text-muted)]">
        {t('calendarHeatmap.scale.less', { defaultValue: 'Less' })}
      </span>
      <div className="flex items-center gap-1">
        {[0, 0.2, 0.4, 0.6, 0.85, 1.0].map((v) => (
          <div
            key={v}
            className="w-2.5 h-2.5 rounded-sm"
            style={{ background: intensityFill(v, v > 0) }}
          />
        ))}
      </div>
      <span className="text-[10px] text-[var(--color-text-muted)]">
        {t('calendarHeatmap.scale.more', { defaultValue: 'More' })}
      </span>
    </div>
  );
}

// ===========================================================================
// Helpers
// ===========================================================================

const CELL = 11;
const GAP = 3;
const ROW_LABEL_W = 26;
const DAY_LABEL_H = 16;
const DAY_LABELS = ['Mon', '', 'Wed', '', 'Fri', '', '']; // ISO Mon=0
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function buildGrid(days) {
  // Build a day lookup
  const lookup = {};
  for (const d of days) lookup[d.date] = d;

  // Find the start (leftmost column = week containing the earliest date)
  // For simplicity, snap to the Monday of the first day
  if (days.length === 0) {
    return { cells: [], cols: 53, monthLabels: [] };
  }
  const firstDate = new Date(days[0].date + 'T00:00:00');
  const firstDayOfWeek = (firstDate.getDay() + 6) % 7;  // ISO: Mon=0
  const startDate = new Date(firstDate);
  startDate.setDate(startDate.getDate() - firstDayOfWeek);

  const lastDate = new Date(days[days.length - 1].date + 'T00:00:00');
  const totalDays = Math.ceil((lastDate - startDate) / (24 * 60 * 60 * 1000)) + 1;
  const cols = Math.ceil(totalDays / 7);

  const cells = [];
  const monthLabels = [];
  let lastMonth = -1;
  for (let col = 0; col < cols; col++) {
    for (let row = 0; row < 7; row++) {
      const idx = col * 7 + row;
      const cellDate = new Date(startDate);
      cellDate.setDate(startDate.getDate() + idx);
      const dateStr = cellDate.toISOString().slice(0, 10);
      const inRange = cellDate >= firstDate && cellDate <= lastDate;
      const day = inRange ? lookup[dateStr] : null;
      cells.push({ col, row, day });

      // Month label: first row that starts a new month
      if (row === 0) {
        const month = cellDate.getMonth();
        if (month !== lastMonth) {
          monthLabels.push({ x: col * (CELL + GAP), label: MONTHS[month] });
          lastMonth = month;
        }
      }
    }
  }
  return { cells, cols, monthLabels };
}

function intensityFill(intensity, hasWorkout) {
  if (!hasWorkout || intensity <= 0) {
    return 'rgba(255, 255, 255, 0.04)';
  }
  // Same lime primary as MuscleMap for visual coherence
  const alpha = 0.2 + intensity * 0.7;
  return `rgba(213, 255, 123, ${alpha.toFixed(3)})`;
}

function formatDate(iso, t) {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Re-export ColorScale so the page can use it for the legend
export { ColorScale };

export default CalendarHeatmap;
